import { ADPAD, EMERON, type Theme } from './palette';

/**
 * The themes that ship. ADPAD Navy is the signed-off look; the others exist for real reasons rather than for variety.
 *
 * None of them touches the printed documents. An RV, an F03/F04 tag and the letters are controlled formats ADPAD files
 * on paper — they stay black on white whichever of these is chosen.
 */

/**
 * Night shift. The stores run around the clock and the hangar office is dark at 03:00; a white screen at that hour is
 * genuinely unpleasant to work under. It keeps the department's navy as the identity rather than becoming generic
 * grey, and lifts the status colours enough to hold their meaning on a dark ground — a red that reads as urgent on
 * white turns muddy on near-black, so unserviceable is brightened rather than reused.
 */
export const NIGHT: Theme = {
  ...ADPAD,
  name: 'Night shift',
  dark: true,
  colors: {
    ...ADPAD.colors,
    navy: '#0C1A2E',
    navy2: '#16304F',
    navy3: '#070F1C',
    gold: '#D2AC58',
    gold2: '#B08F3C',
    goldHover: '#E0BE6E',
    onGold: '#17120A',

    bg: '#0E1520',
    paper: '#161E2B',
    ink: '#E4E9F1',
    muted: '#96A3B6',
    line: '#2A3547',
    line2: '#1D2635',

    sbBg: '#0C1A2E',
    sbLine: 'rgba(255,255,255,.10)',
    sbActive: 'rgba(255,255,255,.10)',
    sbText: '#C2CFE2',
    sbTextDim: '#8496AF',
    sbTextStrong: '#FFFFFF',
    sbHeading: '#A7B7CD',
    sbHover: 'rgba(255,255,255,.10)',

    svc: '#4CB963',
    svcBg: '#16301F',
    svcInk: '#8FE0A2',
    svcBorder: '#1F4A2B',
    us: '#F0553F',
    usBg: '#361A16',
    usInk: '#FF9C8B',
    usBorder: '#6B2C22',
    quar: '#E9A82C',
    quarBg: '#33270D',
    quarInk: '#F5CE7A',
    quarBorder: '#5C4715',
    goldBg: '#2C2413',
    info: '#4F92D6',
    infoBg: '#152535',
    infoInk: '#93C2EC',
    infoBorder: '#1C3A5E',

    rowHead: '#1B2433',
    rowHover: '#1E293A',
    rowAlt: '#182130',
    rowBad: '#2A1714',
    rowWarn: '#2A2311',
    fieldBorder: '#33415A',
    placeholder: '#6E7C92',

    scanBg: '#05090F',
  },
};

/**
 * High contrast. Government accessibility procurement usually asks for it, and the stores floor is a bright place —
 * a screen carried out to a hangar door in Abu Dhabi daylight loses every soft tint. Ink is black, borders are heavy
 * and dark, and the tinted backgrounds go white so text never sits on a wash: colour still carries meaning through
 * the foreground and the border, never through fill alone, which is also what a colour-blind reader needs.
 */
export const HIGH_CONTRAST: Theme = {
  ...ADPAD,
  name: 'High contrast',
  dark: false,
  radius: 2,
  colors: {
    ...ADPAD.colors,
    navy: '#00142E',
    navy2: '#002C5F',
    navy3: '#000B1A',
    gold: '#8A6200',
    gold2: '#6B4C00',
    goldHover: '#A87800',
    onGold: '#FFFFFF',

    bg: '#FFFFFF',
    paper: '#FFFFFF',
    ink: '#000000',
    muted: '#3A3A3A',
    line: '#000000',
    line2: '#767676',

    sbBg: '#00142E',
    sbLine: '#FFFFFF',
    sbActive: 'rgba(255,255,255,.22)',
    sbText: '#FFFFFF',
    sbTextDim: '#D8DEE8',
    sbTextStrong: '#FFFFFF',
    sbHeading: '#FFFFFF',
    sbHover: 'rgba(255,255,255,.22)',

    svc: '#005C1E',
    svcBg: '#FFFFFF',
    svcInk: '#004718',
    svcBorder: '#005C1E',
    us: '#A50000',
    usBg: '#FFFFFF',
    usInk: '#8B0000',
    usBorder: '#A50000',
    quar: '#7A4A00',
    quarBg: '#FFFFFF',
    quarInk: '#5E3900',
    quarBorder: '#7A4A00',
    goldBg: '#FFFFFF',
    info: '#00408A',
    infoBg: '#FFFFFF',
    infoInk: '#003370',
    infoBorder: '#00408A',

    rowHead: '#EDEDED',
    rowHover: '#E4E4E4',
    rowAlt: '#F5F5F5',
    rowBad: '#FFFFFF',
    rowWarn: '#FFFFFF',
    fieldBorder: '#000000',
    placeholder: '#4A4A4A',

    scanBg: '#000000',
  },
};

/** Every theme that ships, keyed by the id stored against the system and against a user. */
export const THEMES: Readonly<Record<string, Theme>> = {
  emeron: EMERON,
  adpad: ADPAD,
  night: NIGHT,
  contrast: HIGH_CONTRAST,
};

/** The installation wears the EmeronCore look by default (ADR-013); ADPAD Navy stays on the picker for anyone who prefers it. */
export const DEFAULT_THEME_ID = 'emeron';

/** The theme for an id, falling back to ADPAD's own rather than to nothing. */
export const themeFor = (id: string | null | undefined): Theme => THEMES[id ?? ''] ?? EMERON;
